import { repeat } from "@@/utilities/repeat";

import { type BookableSettings } from "../../acquire/bookable-settings";

import { bookableOptionPriceCalculator } from "../../acquire/resource-reference-options/bookable-option-price-calculator";
import { isQuantityPickerBookableOptionValue } from "../../acquire/resource-reference-options/quantity-picker-bookable-option-value";
import { consolidateResourceRequestsOptionPerResourceReference } from "../../acquire/resources/resource-helper";
import type { ResourceReservationRequest } from "../../acquire/resources/resource-reservation-request";

import { getSkuDescription } from "../../products/get-sku-description";
import type { Product } from "../../products/product";
import {
    isBookableSku,
    isBuyableSku,
    isGiftCardSku,
    isVoucherSku,
    type Sku,
} from "../../products/sku";

import { type ExtraGroupId } from "@@/shared/acquire/extras-group";
import { isAccommodation } from "@@/shared/acquire/is-accommodation";
import { isBookable } from "@@/shared/acquire/is-bookable";
import { now } from "@@/shared/date-time/date-time";
import { type TimeRange } from "@@/shared/date-time/time-range";
import {
    inMinutes,
    type DurationInMinutes,
} from "@@/shared/date-time/time-spans";
import { type Discount } from "@@/shared/discounts/discount";
import { calculateDiscountedPriceOnOptionValue } from "@@/shared/discounts/discount-types/discount-calculator";
import {
    metaContentFactory,
    type MetaContent,
} from "@@/shared/meta-content/meta-content";
import { type PickedOptional } from "@@/shared/optionals/picked-optional";
import {
    orderItemOptionalFactory_V2,
    orderItemReservationFactory_V2,
    type OrderItemBookable_V2,
    type OrderItemBuyable_V2,
    type OrderItemGiftCard_V2,
    type OrderItemVoucher_V2,
    type OrderItem_V2,
} from "@@/shared/orders/order-items/order-item-v2";
import { zeroPrice, type Price } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import { type Provider } from "@@/shared/providers/provider";
import { type Quantity } from "@@/shared/units-and-quantities/quantity";
import { isOrderItemBookable_V2 } from "./is-order-item-v2";
import { orderItemIdFactory_V2, type OrderItemId_V2 } from "./order-item-id";

const orderItemParentFactory_V2 = (params: {
    sku: Sku;
    discounts: Discount[];
    product: Product;
    messageToProvider: string | undefined;
    pricePerItem: Price;
    price: Price;
    quantity: Quantity;
    time: TimeRange | undefined;
    settings: BookableSettings | undefined;
    parent: OrderItemId_V2 | undefined;
    extra?: {
        _id: ExtraGroupId;
        meta: MetaContent;
    };
}) => {
    const {
        sku,
        discounts,
        product,
        messageToProvider,
        price,
        pricePerItem,
        quantity,
        time,
        settings,
    } = params;

    /**
     * create a unique but reproducible id
     * for every unique cart item.
     * uniqueness based on sku id and picked option ids
     */

    const orderItemV2Id = orderItemIdFactory_V2();

    switch (sku.acquire._type) {
        case "BUYABLE": {
            if (!isBuyableSku(sku))
                throw new Error(
                    `Invalid sku, not a buyable; ${sku._id}, ${product._id}`,
                );
            const orderItem: OrderItemBuyable_V2 = {
                _id: orderItemV2Id,
                _type: "ORDER_ITEM_V2",
                _createdAt: now(),
                acquireType: sku.acquire._type,
                skuId: sku._id,
                productId: sku.productId,
                discounts,
                providerId: product.providerId,
                articleNumber: sku.articleNumber
                    ? sku.articleNumber
                    : undefined,
                messageToProvider: messageToProvider || null,
                meta: metaContentFactory({
                    ...sku.meta,
                    title: product.meta.title,
                    kicker: product.meta.kicker,
                    subtitle: sku.meta.title || product.meta.title,
                    description: getSkuDescription(sku),
                }),
                pricePerItem: pricePerItem,
                price: price,
                originalPricePerItem: pricePerItem,
                originalPrice: price,
                quantity,
                settings: {
                    orderItemStatusEnabled:
                        !!product.handleStatusPerOrderedItem,
                },
                availability: product.availability
                    ? product.availability
                    : undefined,
                trackStockBalance: sku.trackStockBalance,
                parentId: params.parent,
                extraGroup: params.extra,
            };
            return orderItem;
        }
        case "BOOKABLE": {
            if (!time || !settings) throw new Error("NO RESERVATION");
            if (!isBookableSku(sku))
                throw new Error(
                    `Invalid sku, not a bookable; ${sku._id}, ${product._id}`,
                );
            const orderItemBook: OrderItemBookable_V2 = {
                _id: orderItemV2Id,
                _type: "ORDER_ITEM_V2",
                _createdAt: now(),
                acquireType: sku.acquire._type,
                skuId: sku._id,
                productId: sku.productId,
                bookingType: sku.acquire.type ?? "GENERIC",
                time: time,
                discounts,
                providerId: product.providerId,
                articleNumber: sku.articleNumber
                    ? sku.articleNumber
                    : undefined,
                messageToProvider: messageToProvider || null,
                meta: metaContentFactory({
                    ...sku.meta,
                    title: product.meta.title,
                    kicker: product.meta.kicker,
                    subtitle: sku.meta.title || product.meta.title,
                    description: getSkuDescription(sku),
                }),
                pricePerItem: pricePerItem,
                price: price,
                originalPricePerItem: pricePerItem,
                originalPrice: price,
                quantity,
                settings: settings,
                availability: product.availability
                    ? product.availability
                    : undefined,
                hasAccess: !!sku.acquire.hasAccess,
            };

            return orderItemBook;
        }
        case "VOUCHER": {
            if (!isVoucherSku(sku))
                throw new Error(
                    `Invalid sku, not a voucher; ${sku._id}, ${product._id}`,
                );

            const orderItem: OrderItemVoucher_V2 = {
                _id: orderItemV2Id,
                _type: "ORDER_ITEM_V2",
                _createdAt: now(),
                acquireType: sku.acquire._type,
                skuId: sku._id,
                productId: sku.productId,
                discounts,
                providerId: product.providerId,
                articleNumber: sku.articleNumber
                    ? sku.articleNumber
                    : undefined,
                messageToProvider: messageToProvider || null,
                meta: metaContentFactory({
                    ...sku.meta,
                    title: product.meta.title,
                    kicker: product.meta.kicker,
                    subtitle: sku.meta.subtitle || product.meta.subtitle,
                    description: product.meta.description,
                    images: product.meta.images, //maybe sku but we don't have it.
                }),
                amount: sku.acquire.amount,
                pricePerItem: pricePerItem,
                price: price,
                originalPricePerItem: pricePerItem,
                originalPrice: price,
                quantity,
                availability: product.availability
                    ? product.availability
                    : undefined,
                trackStockBalance: sku.trackStockBalance,
                hasTicket:
                    sku.acquire.disableTicketsGeneration === undefined
                        ? true
                        : !sku.acquire.disableTicketsGeneration,
            };
            return orderItem;
        }
        case "GIFTCARD": {
            if (!isGiftCardSku(sku))
                throw new Error(
                    `Invalid sku, not a gift card; ${sku._id}, ${product._id}`,
                );

            const orderItem: OrderItemGiftCard_V2 = {
                _id: orderItemV2Id,
                _type: "ORDER_ITEM_V2",
                _createdAt: now(),
                acquireType: sku.acquire._type,
                skuId: sku._id,
                productId: sku.productId,
                discounts,
                providerId: product.providerId,
                articleNumber: sku.articleNumber
                    ? sku.articleNumber
                    : undefined,
                messageToProvider: messageToProvider || null,
                meta: metaContentFactory({
                    ...sku.meta,
                    title: product.meta.title,
                    kicker: product.meta.kicker,
                    subtitle: sku.meta.subtitle || product.meta.subtitle,
                    description: product.meta.description,
                    images: product.meta.images, //maybe sku but we don't have it.
                }),
                amount: sku.acquire.amount,
                pricePerItem: pricePerItem,
                price: price,
                originalPricePerItem: pricePerItem,
                originalPrice: price,
                quantity,
                availability: product.availability
                    ? product.availability
                    : undefined,
            };
            return orderItem;
        }
        case "ACCOMMODATION": {
            throw new Error("ACCOMMODATION NOT IMPLEMENTED");
        }
        default:
            throw new Error(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                "Unhandled acquire type; " + (sku.acquire as any)._type,
            );
    }
};

const orderItemsFromSku_V2 = (params: {
    sku: Sku;
    product: Product;
    provider: Provider;
    discounts: Discount[];
    quantity: Quantity;
    pickedOptions: PickedOptional[];
    messageToProvider?: string;
    time?: TimeRange;
    bookableSettings?: BookableSettings;
    resourceReservations?: ResourceReservationRequest[];
    //Needed?
    parentId?: OrderItemId_V2;
    extra?: {
        _id: ExtraGroupId;
        meta: MetaContent;
    };
}): OrderItem_V2[] => {
    // Parse all prices from sku and picked options
    // their combined sum will be the price of the cart item
    const {
        sku,
        product,
        quantity,
        pickedOptions,
        messageToProvider,
        discounts,
        time,
        resourceReservations,
        bookableSettings,
    } = params;

    const pricePerItem = params.sku.acquire.price;

    const numberOfBookedTime = time
        ? inMinutes({ seconds: time.duration })
        : (0 as DurationInMinutes);

    const resourcePrice = sumPrices(
        (resourceReservations ?? []).flatMap(booking =>
            booking.optionValues.flatMap(optionValue =>
                bookableOptionPriceCalculator({
                    bookedMinutes: numberOfBookedTime,
                    quantity: isQuantityPickerBookableOptionValue(optionValue)
                        ? optionValue.quantity.value
                        : 0,
                    option: optionValue,
                }),
            ),
        ),
    );
    const price =
        product.acquirableAs !== "BOOKABLE"
            ? sumPrices(repeat(pricePerItem, quantity.value))
            : zeroPrice;

    const item = orderItemParentFactory_V2({
        sku,
        discounts,
        product,
        messageToProvider,
        pricePerItem,
        price,
        quantity,
        time,
        settings: bookableSettings,
        parent: params.parentId,
        extra: params.extra,
    });

    const parentTotalPrice =
        product.acquirableAs !== "BOOKABLE"
            ? sumPrices(repeat(pricePerItem, quantity.value))
            : sumPrices([
                  pricePerItem, //Original Price (Base price)
                  resourcePrice,
              ]);

    const pickedOptionals = pickedOptions.map(pickedOption =>
        orderItemOptionalFactory_V2({
            pickedOption,
            discounts,

            messageToProvider,
            productPricePerItem: pricePerItem,
            productPrice: parentTotalPrice,
            quantity,
            parent: item,
        }),
    );

    if (isBookable(product)) {
        const consolidated =
            consolidateResourceRequestsOptionPerResourceReference(
                resourceReservations ?? [],
            );

        const reservations = consolidated.flatMap(value => {
            if (isQuantityPickerBookableOptionValue(value)) {
                if (!time) return [];
                if (!isOrderItemBookable_V2(item)) return [];

                const isMain = isBookable(sku.acquire)
                    ? sku.acquire.main._id === value.resourceReferenceId
                    : false;

                const item2 = orderItemReservationFactory_V2({
                    value,
                    discounts,
                    messageToProvider,
                    quantity,
                    time,
                    parent: item,
                    main: isMain,
                });
                if (discounts.length > 0) {
                    const price = calculateDiscountedPriceOnOptionValue(
                        discounts,
                    )(item, value);
                    return {
                        ...item2,
                        price: price.totalPrice,
                        pricePerItem: price.pricePer,
                    };
                }
                return item2;
            }

            return [];
        });
        return [item, ...pickedOptionals, ...reservations];
    }

    if (isAccommodation(product)) {
        if (!time) throw new Error("NO TIME");

        //
    }

    // Buyable, gift card, vouchers
    return [item, ...pickedOptionals];
};

export { orderItemsFromSku_V2 };
